import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { AutoVideo } from "@/components/AutoVideo";

const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

// ✅ NEW: normalize copy so it never renders with giant gaps / weird line breaks
const cleanHeroCopy = (s: string) =>
  (s || "")
    .replace(/\r\n/g, "\n")
    .replace(/\n+/g, " ") // remove line breaks
    .replace(/\s{2,}/g, " ") // collapse extra spaces
    .trim();

const Index = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const reduce = useReducedMotion();
  const [activePortal, setActivePortal] = useState<number>(0);
  const [transitioning, setTransitioning] = useState<string | null>(null);

  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const heroRef = useRef<HTMLDivElement>(null);
  const portalSectionRef = useRef<HTMLDivElement>(null);
  const isTouch = useMemo(() => isTouchDevice(), []);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const { scrollYProgress: portalProgress } = useScroll({
    target: portalSectionRef,
    offset: ["start end", "end start"], // Start when section enters viewport from bottom
  });

  // Hero transforms - drift up, fade
  const heroTitleY = useTransform(heroProgress, [0, 0.5], [0, reduce || isTouch ? 0 : -100]);
  const heroTitleOpacity = useTransform(heroProgress, [0, 0.4], [1, 0]);

  // Portal section - animations trigger early as section enters viewport
  const headerOpacity = useTransform(portalProgress, [0, 0.08, 0.15], [0, 1, 1]);
  const headerY = useTransform(portalProgress, [0, 0.1], reduce ? [0, 0] : [40, 0]);
  const portalContainerOpacity = useTransform(portalProgress, [0.05, 0.15], [0, 1]);

  useEffect(() => {
    const unsubscribe = portalProgress.on("change", (v) => {
      // Evenly distribute portals across full scroll range (0-1)
      if (v < 0.33) setActivePortal(0);
      else if (v < 0.66) setActivePortal(1);
      else setActivePortal(2);
    });
    return unsubscribe;
  }, [portalProgress]);

  const directions = [
    {
      id: "photo",
      title: t("home.quickpick.directions.social.title") || "Social Direction",
      tagline: t("home.quickpick.directions.social.tagline") || "Aesthetic-driven image package.",
      route: "/quick-picks?tab=photo",
      video: "/videos/SocialDirection.mp4",
    },
    {
      id: "video",
      title: t("home.quickpick.directions.cinematic.title") || "Cinematic Creator",
      tagline: t("home.quickpick.directions.cinematic.tagline") || "Intention-led video package.",
      route: "/quick-picks?tab=video",
      video: "/videos/cinematiccreator.mp4",
    },
    {
      id: "combo",
      title: t("home.quickpick.directions.suite.title") || "Full Cinematic Suite",
      tagline: t("home.quickpick.directions.suite.tagline") || "The full package.",
      route: "/quick-picks?tab=combo",
      video: "/videos/Fullcinematicsuite.mp4",
    },
  ];

  // ✅ NEW: clean subtitle once, then reuse in both hero variants
  const heroSubtitle = useMemo(() => cleanHeroCopy(t("home.hero.subtitle") || ""), [t]);

  // Ensure active portal video plays on scroll (desktop only)
  useEffect(() => {
    if (isTouch) return;
    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (video) {
        if (key === directions[activePortal]?.id) video.play().catch(() => {});
        else video.pause();
      }
    });
  }, [activePortal, isTouch]); // directions is stable enough here; keep minimal change

  const handlePortalNavigate = useCallback(
    (direction: (typeof directions)[0]) => {
      setTransitioning(direction.id);
      setTimeout(() => navigate(direction.route), 500);
    },
    [navigate],
  );

  const handlePortalHover = useCallback(
    (index: number) => {
      if (isTouch) return;
      setActivePortal(index);

      Object.entries(videoRefs.current).forEach(([key, video]) => {
        if (video) {
          if (key === directions[index].id) video.play().catch(() => {});
          else video.pause();
        }
      });
    },
    [isTouch, directions],
  );

  return (
    <div className="bg-black text-white font-sans">
      <Navbar />

      {/* HERO - Pure Cinematic Opening */}
      <section ref={heroRef} className="relative w-screen h-[100svh] m-0 p-0 overflow-hidden">
        {/* Video plays on all devices - full bleed with AutoVideo */}
        <AutoVideo src="/videos/hero-website.mp4" preload="auto" />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/35 to-black z-10" />

        {/* Title */}
        <div className="relative z-10 h-full flex items-end pb-24 md:pb-40 px-6 md:px-12 lg:px-20">
          {isTouch ? (
            <div className="max-w-4xl">
              <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-serif font-light tracking-tight text-white leading-[0.9] mb-5 break-words">
                {t("home.hero.titleLine1")}
                <br />
                {t("home.hero.titleLine2")}
              </h1>

              {/* ✅ UPDATED: tighter, cleaner, no weird gaps */}
              <p className="text-base md:text-xl text-white/55 font-light leading-snug max-w-lg">{heroSubtitle}</p>
            </div>
          ) : (
            <motion.div
              style={{ y: heroTitleY, opacity: heroTitleOpacity }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className="max-w-4xl"
            >
              <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-serif font-light tracking-tight text-white leading-[0.9] mb-6 break-words">
                {t("home.hero.titleLine1")}
                <br />
                {t("home.hero.titleLine2")}
              </h1>

              {/* ✅ UPDATED: tighter, cleaner, no weird gaps */}
              <p className="text-xl md:text-2xl text-white/50 font-light leading-snug max-w-2xl">{heroSubtitle}</p>
            </motion.div>
          )}
        </div>

        {/* Scroll indicator - desktop only */}
        {!isTouch && !reduce && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="absolute bottom-12 left-6 md:left-12 lg:left-20"
          >
            <motion.div
              className="w-5 h-8 rounded-full border border-white/25 flex justify-center pt-1.5"
              animate={{ y: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            >
              <div className="w-0.5 h-1.5 bg-white/40 rounded-full" />
            </motion.div>
          </motion.div>
        )}
      </section>

      {/* DIRECTION PANELS - Clean, clickable-only */}
      {isTouch ? (
        /* Mobile: Full-bleed stacked panels with scroll-snap - film strip feel */
        <section className="relative bg-black" style={{ scrollSnapType: "y mandatory" }}>
          {/* Section header - fixed position overlay */}
          <div className="sticky top-0 z-20 px-6 pt-20 pb-8 bg-gradient-to-b from-black via-black/90 to-transparent pointer-events-none">
            <h2 className="text-3xl font-serif font-light tracking-tight text-white leading-[0.95] mb-2">
              {t("quickpick.arrival.title") || "Choose a direction"}
            </h2>
            <p className="text-sm text-white/40 font-light">
              {t("quickpick.arrival.subtitle") || "An entry point, not a decision."}
            </p>
          </div>

          {/* Full-bleed panels */}
          <div className="flex flex-col">
            {directions.map((direction, index) => (
              <div
                key={direction.id}
                onClick={() => navigate(direction.route)}
                className="relative min-h-[85svh] w-full overflow-hidden cursor-pointer active:opacity-90 transition-opacity"
                style={{ scrollSnapAlign: "start" }}
              >
                {/* Full-bleed video background */}
                <AutoVideo src={direction.video} preload="metadata" />

                {/* Subtle gradient overlays for readability - not heavy black */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

                {/* Content positioned at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 pb-12">
                  <span className="block text-xs font-serif tracking-[0.4em] text-white/40 mb-3">0{index + 1}</span>
                  <h3 className="text-3xl font-serif font-light text-white mb-2">{direction.title}</h3>
                  <p className="text-sm text-white/50 font-light line-clamp-1 max-w-xs">{direction.tagline}</p>
                </div>

                {/* Hairline separator */}
                {index < directions.length - 1 && <div className="absolute bottom-0 left-6 right-6 h-px bg-white/10" />}
              </div>
            ))}
          </div>
        </section>
      ) : (
        /* Desktop: Full scroll-driven experience - NO ENTER, NO NEXT */
        <section ref={portalSectionRef} className="relative bg-black min-h-[100svh]">
          <div className="h-[100svh] overflow-hidden">
            <AnimatePresence>
              {transitioning && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="fixed inset-0 z-50 bg-black"
                  transition={{ duration: 0.5 }}
                />
              )}
            </AnimatePresence>

            {/* Header */}
            <motion.div
              className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 lg:px-20 pt-28 md:pt-36"
              style={{ opacity: headerOpacity, y: headerY }}
            >
              <h2 className="text-6xl md:text-7xl lg:text-8xl font-serif font-light tracking-tight text-white leading-[0.95] mb-4">
                {t("quickpick.arrival.title") || "Choose a direction"}
              </h2>
              <p className="text-lg text-white/30 font-light">
                {t("quickpick.arrival.subtitle") || "An entry point, not a decision."}
              </p>
            </motion.div>

            {/* Panels - Clickable only, no CTAs */}
            <motion.div className="absolute inset-0" style={{ opacity: portalContainerOpacity }}>
              <div className="w-full h-full flex flex-row">
                {directions.map((direction, index) => {
                  const isActive = activePortal === index;

                  return (
                    <motion.div
                      key={direction.id}
                      className="relative overflow-hidden cursor-pointer"
                      onClick={() => handlePortalNavigate(direction)}
                      onMouseEnter={() => handlePortalHover(index)}
                      animate={{ flex: isActive ? 1.25 : 0.875 }}
                      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {/* Video */}
                      <motion.div
                        className="absolute inset-0"
                        animate={{ scale: isActive ? 1 : 1.03 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <video
                          ref={(el) => {
                            videoRefs.current[direction.id] = el;
                          }}
                          autoPlay={index === 0}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        >
                          <source src={direction.video} type="video/mp4" />
                        </video>
                      </motion.div>

                      {/* Constant vignette */}
                      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/70 via-black/25 to-black/35" />

                      {/* Inactive dim */}
                      <motion.div
                        className="absolute inset-0 pointer-events-none"
                        animate={{ opacity: isActive ? 0 : 1 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        style={{ background: "rgba(0,0,0,0.38)" }}
                      />

                      {/* Hairline divider */}
                      {index < directions.length - 1 && (
                        <div className="absolute top-0 right-0 w-px h-full bg-white/5 pointer-events-none" />
                      )}

                      {/* Caption - title + 1-line tagline only */}
                      <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 lg:p-14 pr-28">
                        <motion.h3
                          className="text-3xl md:text-4xl font-serif font-light tracking-tight text-white leading-tight mb-3"
                          animate={{ opacity: isActive ? 1 : 0.55, y: isActive ? 0 : 8 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {direction.title}
                        </motion.h3>

                        <motion.p
                          className="text-sm font-light text-white/40 max-w-sm line-clamp-1"
                          animate={{ opacity: isActive ? 0.75 : 0.25, y: isActive ? 0 : 14 }}
                          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {direction.tagline}
                        </motion.p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Progress dots */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3">
              {directions.map((_, index) => (
                <motion.div
                  key={index}
                  className="w-1.5 h-1.5 rounded-full bg-white/25"
                  animate={{
                    scale: activePortal === index ? 1.4 : 1,
                    opacity: activePortal === index ? 1 : 0.3,
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Index;
